/**
 * Product reviews excerpt — fetches this product's Reviews.io reviews
 * and renders the branded summary + cards. Polarise stays on /pages/reviews.
 */
(function () {
  const API_BASE = 'https://api.reviews.io/reviews';
  const STAR_SVG =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 2.5l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.8 6.2 20.4l1.1-6.5L2.6 9.3l6.5-.9L12 2.5z"/></svg>';

  function formatCount(n) {
    try {
      return new Intl.NumberFormat('en-GB').format(n);
    } catch (e) {
      return String(n);
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function displayName(raw) {
    if (!raw) return 'Verified buyer';
    const parts = String(raw).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0];
    const last = parts[parts.length - 1];
    const initial = last.charAt(0).toUpperCase();
    return initial ? parts[0] + ' ' + initial + '.' : parts[0];
  }

  function attrMap(attributes) {
    const map = {};
    if (!Array.isArray(attributes)) return map;
    attributes.forEach(function (attr) {
      if (!attr || !attr.label || attr.value == null || attr.value === '') return;
      const key = String(attr.label).toLowerCase();
      if (!map[key]) map[key] = String(attr.value).trim();
    });
    return map;
  }

  function detailLine(review) {
    const attrs = attrMap(review.author && review.author.attributes);
    const breed =
      attrs['what breed is your dog?'] ||
      attrs['dog breed'] ||
      attrs['breed'] ||
      '';
    const age =
      attrs['how old is your dog?'] ||
      attrs['dog age'] ||
      attrs['age'] ||
      '';

    if (breed && age) {
      const ageLabel =
        /\d/.test(age) && !/yr|year|month|mth|week|old/i.test(age) ? age + ' yrs' : age;
      return breed + ', ' + ageLabel;
    }
    if (breed) return breed;
    if (review.author && review.author.location) return review.author.location;
    return '';
  }

  function uniqueSkus(raw) {
    const seen = Object.create(null);
    const out = [];
    String(raw || '')
      .split(';')
      .forEach(function (part) {
        const sku = String(part || '').trim();
        if (!sku || seen[sku]) return;
        seen[sku] = true;
        out.push(sku);
      });
    return out;
  }

  function pickReviews(reviews, limit, minLength) {
    const seen = Object.create(null);
    const picked = [];

    (reviews || []).forEach(function (review) {
      if (picked.length >= limit) return;
      if (!review) return;

      const comments = String(review.comments || '').trim();
      if (comments.length < minLength) return;

      const authorKey = String((review.author && review.author.name) || review.id || '')
        .toLowerCase()
        .trim();
      if (authorKey && seen[authorKey]) return;
      if (authorKey) seen[authorKey] = true;
      picked.push(review);
    });

    return picked;
  }

  function ratingInt(value) {
    const n = Math.round(Number(value) || 0);
    return Math.max(0, Math.min(5, n));
  }

  function starsHtml(rating) {
    const filled = ratingInt(rating);
    let html =
      '<div class="reviews-band__stars" aria-label="' +
      filled +
      ' out of 5 stars">';
    for (let i = 1; i <= 5; i++) {
      html +=
        '<span class="' + (i <= filled ? 'is-on' : 'is-off') + '">' + STAR_SVG + '</span>';
    }
    return html + '</div>';
  }

  function histogramRows(ratings) {
    const counts = [5, 4, 3, 2, 1].map(function (star) {
      const raw = ratings && (ratings[star] != null ? ratings[star] : ratings[String(star)]);
      return Number(raw) || 0;
    });
    const total = counts.reduce(function (sum, n) {
      return sum + n;
    }, 0);
    const denom = total || 1;

    return [5, 4, 3, 2, 1].map(function (star, index) {
      return {
        star: star,
        count: counts[index],
        pct: Math.round((counts[index] / denom) * 100),
      };
    });
  }

  function summaryHtml(data, basedOnTemplate) {
    const average = Number(data.average_rating);
    const score = Number.isFinite(average) ? average.toFixed(1) : '—';
    const count = Number(data.review_count) || 0;
    const basedOn = String(basedOnTemplate || 'Based on {count} reviews').replace(
      '{count}',
      formatCount(count)
    );
    const rows = histogramRows(data.stats && data.stats.ratings);

    return (
      '<p class="reviews-band__score">' +
      escapeHtml(score) +
      '</p>' +
      starsHtml(average) +
      '<p class="reviews-band__based-on">' +
      escapeHtml(basedOn) +
      '</p>' +
      '<ul class="reviews-band__histogram" role="list">' +
      rows
        .map(function (row) {
          return (
            '<li class="reviews-band__hist-row">' +
            '<span class="reviews-band__hist-label">' +
            row.star +
            '<span class="reviews-band__hist-star" aria-hidden="true">' +
            STAR_SVG +
            '</span></span>' +
            '<span class="reviews-band__hist-bar" aria-hidden="true"><span style="width:' +
            row.pct +
            '%"></span></span>' +
            '<span class="reviews-band__hist-pct">' +
            row.pct +
            '%</span>' +
            '</li>'
          );
        })
        .join('') +
      '</ul>'
    );
  }

  function cardHtml(review, verifiedLabel) {
    const name = displayName(review.author && review.author.name);
    const detail = detailLine(review);
    const quote = String(review.comments || '').trim();
    const timeAgo = String(review.time_ago || '').trim();
    const verified = Number(review.verified) === 1 || review.verified === true;

    return (
      '<li class="reviews-band__card">' +
      '<div class="reviews-band__card-head">' +
      starsHtml(review.rating) +
      (verified
        ? '<span class="reviews-band__badge">' + escapeHtml(verifiedLabel) + '</span>'
        : '') +
      (timeAgo
        ? '<span class="reviews-band__time">' + escapeHtml(timeAgo) + '</span>'
        : '') +
      '</div>' +
      '<p class="reviews-band__author">' +
      escapeHtml(name) +
      '</p>' +
      (detail
        ? '<p class="reviews-band__detail">' + escapeHtml(detail) + '</p>'
        : '') +
      '<p class="reviews-band__quote">“' +
      escapeHtml(quote) +
      '”</p>' +
      '</li>'
    );
  }

  function buildUrl(store, skus, perPage) {
    const params = new URLSearchParams({
      store: store,
      type: 'product_review',
      sort: 'date_desc',
      per_page: String(perPage),
    });
    if (skus.length) params.set('sku', skus.join(';'));
    return API_BASE + '?' + params.toString();
  }

  async function init(root) {
    const store = root.dataset.store;
    const skus = uniqueSkus(root.dataset.sku);
    const limit = Math.max(1, parseInt(root.dataset.limit || '3', 10) || 3);
    const minLength = Math.max(1, parseInt(root.dataset.minLength || '40', 10) || 40);
    const fetchCount = Math.max(limit * 5, 20);
    const summary = root.querySelector('[data-reviews-summary]');
    const grid = root.querySelector('[data-reviews-grid]');
    const status = root.querySelector('[data-reviews-status]');
    const link = root.querySelector('[data-reviews-link]');
    const linkTemplate = root.dataset.linkLabel || 'Read all {count} reviews →';
    const basedOnTemplate = root.dataset.basedOn || 'Based on {count} reviews';
    const verifiedLabel = root.dataset.verifiedLabel || 'Verified';
    const emptyMessage = root.dataset.emptyMessage || 'Reviews are temporarily unavailable.';
    const loadingMessage = root.dataset.loadingMessage || 'Loading reviews…';

    if (!store || !summary || !grid || !skus.length) {
      root.classList.remove('is-loading');
      root.classList.add('is-empty');
      if (window.Shopify && Shopify.designMode) {
        root.classList.add('is-design-mode');
        if (status) status.textContent = emptyMessage;
      }
      return;
    }

    root.classList.add('is-loading');
    root.classList.remove('is-ready', 'is-empty');
    if (status) status.textContent = loadingMessage;

    try {
      const response = await fetch(buildUrl(store, skus, fetchCount), {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Reviews API ' + response.status);

      const data = await response.json();
      const reviews = pickReviews(data.reviews || [], limit, minLength);

      if (!reviews.length) {
        root.classList.remove('is-loading');
        root.classList.add('is-empty');
        if (window.Shopify && Shopify.designMode) {
          root.classList.add('is-design-mode');
          if (status) status.textContent = emptyMessage;
        }
        return;
      }

      summary.innerHTML = summaryHtml(data, basedOnTemplate);
      grid.innerHTML = reviews
        .map(function (review) {
          return cardHtml(review, verifiedLabel);
        })
        .join('');

      if (link) {
        const countLabel = formatCount(Number(data.review_count) || reviews.length);
        link.textContent = linkTemplate.replace('{count}', countLabel);
        link.hidden = false;
      }

      root.classList.remove('is-loading', 'is-empty');
      root.classList.add('is-ready');
    } catch (err) {
      console.warn('[product-reviews]', err);
      root.classList.remove('is-loading');
      root.classList.add('is-empty');
      if (window.Shopify && Shopify.designMode) {
        root.classList.add('is-design-mode');
        if (status) status.textContent = emptyMessage;
      }
    }
  }

  function boot() {
    document.querySelectorAll('[data-product-reviews]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const root =
      event.target && event.target.querySelector
        ? event.target.querySelector('[data-product-reviews]')
        : null;
    if (root) init(root);
  });
})();
